import React, { useState, useMemo } from 'react';
import { useApiQuery, useApiMutation } from '../hooks/useApiQuery';
import {
  getMealTypes,
  getAttendeeMealStatus,
  assignMeals,
} from '../services/api';
import useToast from '../hooks/useToast';
import {
  MagnifyingGlassIcon,
  CheckIcon,
  XMarkIcon,
  CurrencyRupeeIcon,
  UserIcon,
  CheckCircleIcon,
  NoSymbolIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

const MealAssignmentPanel = ({ eventId }) => {
  const { showSuccess, showError } = useToast();

  const { data: meals = [] } = useApiQuery(
    ['meal-types', eventId],
    () => getMealTypes(eventId).then(r => r.data),
    { enabled: Boolean(eventId) }
  );

  const [attendeeId, setAttendeeId] = useState('');
  const [selected, setSelected] = useState([]);
  const [status, setStatus] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');

  const assignMutation = useApiMutation(
    data => assignMeals(eventId, data),
    {
      onSuccess: res => {
        showSuccess(res.data.message);
        handleLookup();
        setSelected([]);
      },
      onError: e =>
        showError(e.response?.data?.message || 'Failed'),
    }
  );

  const handleLookup = async () => {
    if (!attendeeId.trim()) return;
    setLookupLoading(true);
    setLookupError('');
    try {
      const res = await getAttendeeMealStatus(
        eventId,
        attendeeId.trim()
      );
      setStatus(res.data);
      const unpaid = res.data.meals
        .filter(m => !m.paid && m.isActive)
        .map(m => m.mealTypeId);
      setSelected(unpaid);
    } catch (err) {
      setLookupError(
        err.response?.data?.message || 'Attendee not found'
      );
      setStatus(null);
    } finally {
      setLookupLoading(false);
    }
  };

  const resetLookup = () => {
    setAttendeeId('');
    setStatus(null);
    setSelected([]);
    setLookupError('');
  };

  const toggleMeal = mealTypeId => {
    setSelected(prev =>
      prev.includes(mealTypeId)
        ? prev.filter(id => id !== mealTypeId)
        : [...prev, mealTypeId]
    );
  };

  const totalAmount = useMemo(
    () =>
      meals
        .filter(m => selected.includes(m._id))
        .reduce((sum, m) => sum + (m.price || 0), 0),
    [meals, selected]
  );

  const handleAssign = () => {
    if (!status) return;
    if (selected.length === 0)
      return showError('Select at least one meal');

    assignMutation.mutate({
      attendeeId: status.attendee.customId,
      mealTypeIds: selected,
      paidAmount: totalAmount,
    });
  };

  return (
    <div className="space-y-6">
      {/* =================================================
          LOOKUP CARD
      ================================================= */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-2">
            <UserIcon className="w-4 h-4 text-[#1672b9]" />
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Attendee Lookup
            </p>
          </div>
        </div>

        <div className="p-5 space-y-3">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                value={attendeeId}
                onChange={e => setAttendeeId(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleLookup();
                  }
                }}
                placeholder="Enter attendee custom ID"
                className="w-full pl-10 pr-3 py-3 text-sm font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                autoFocus
                disabled={lookupLoading}
              />
            </div>
            <button
              onClick={handleLookup}
              disabled={lookupLoading || !attendeeId.trim()}
              className="px-6 py-3 bg-[#1672b9] text-white rounded-lg text-sm font-medium hover:bg-[#0f5a9e] disabled:opacity-50 transition-colors inline-flex items-center gap-2"
            >
              {lookupLoading ? (
                <>
                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                  Searching
                </>
              ) : (
                <>
                  <MagnifyingGlassIcon className="w-4 h-4" />
                  Look up
                </>
              )}
            </button>
          </div>

          {lookupError && (
            <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
              <XMarkIcon className="w-4 h-4 flex-shrink-0" />
              {lookupError}
            </div>
          )}

          {!status && !lookupError && (
            <p className="text-xs text-gray-400">
              Press Enter or click Look up to fetch attendee details
            </p>
          )}
        </div>
      </div>

      {/* =================================================
          ATTENDEE DETAILS + MEALS
      ================================================= */}
      {status && (
        <>
          {/* Attendee card */}
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#1672b9]/10 flex items-center justify-center">
                  <span className="text-[#1672b9] font-semibold text-lg">
                    {status.attendee.name
                      ?.charAt(0)
                      ?.toUpperCase() || '?'}
                  </span>
                </div>
                <div>
                  <p className="text-base font-semibold text-gray-900">
                    {status.attendee.name}
                  </p>
                  <p className="text-xs text-gray-500 font-mono mt-0.5">
                    {status.attendee.customId}
                  </p>
                  <p className="text-xs text-gray-500">
                    {status.attendee.email}
                  </p>
                </div>
              </div>

              <button
                onClick={resetLookup}
                className="text-sm font-medium text-[#1672b9] hover:underline"
              >
                Change
              </button>
            </div>
          </div>

          {/* Meals section */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Select Meals
              </p>
              <p className="text-xs text-gray-500">
                {selected.length} of{' '}
                {
                  status.meals.filter(m => m.isActive && !m.paid)
                    .length
                }{' '}
                selectable
              </p>
            </div>

            <div className="divide-y divide-gray-100">
              {status.meals.map(m => {
                const isChecked = selected.includes(m.mealTypeId);
                const disabled = m.paid || !m.isActive;

                return (
                  <button
                    key={m.mealTypeId}
                    type="button"
                    onClick={() =>
                      !disabled && toggleMeal(m.mealTypeId)
                    }
                    disabled={disabled}
                    className={`w-full text-left px-5 py-4 transition-all duration-200 flex items-center gap-4 ${
                      disabled
                        ? 'cursor-not-allowed bg-gray-50/60'
                        : isChecked
                        ? 'bg-[#1672b9]/[0.04] hover:bg-[#1672b9]/[0.07]'
                        : 'hover:bg-gray-50/60'
                    }`}
                  >
                    {/* Custom animated checkbox */}
                    <div className="flex-shrink-0">
                      <div
                        className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all duration-200 ${
                          disabled
                            ? 'border-gray-200 bg-gray-100'
                            : isChecked
                            ? 'border-[#1672b9] bg-[#1672b9] scale-100'
                            : 'border-gray-300 bg-white'
                        }`}
                      >
                        <CheckIcon
                          className={`w-4 h-4 text-white transition-all duration-200 ${
                            isChecked && !disabled
                              ? 'opacity-100 scale-100'
                              : 'opacity-0 scale-50'
                          }`}
                        />
                      </div>
                    </div>

                    {/* Meal name + price */}
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium transition-colors ${
                          disabled
                            ? 'text-gray-400'
                            : 'text-gray-900'
                        }`}
                      >
                        {m.mealName}
                      </p>
                      <p
                        className={`text-xs mt-0.5 ${
                          disabled
                            ? 'text-gray-400'
                            : 'text-gray-500'
                        }`}
                      >
                        ₹{m.price}
                      </p>
                    </div>

                    {/* Status badge */}
                    <div className="flex-shrink-0">
                      {m.paid ? (
                        m.redeemed ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 border border-red-100 px-2.5 py-1 rounded-full">
                            <CheckCircleIcon className="w-3 h-3" />
                            Used
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-full">
                            <CheckCircleIcon className="w-3 h-3" />
                            Paid
                          </span>
                        )
                      ) : !m.isActive ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-100 border border-gray-200 px-2.5 py-1 rounded-full">
                          <NoSymbolIcon className="w-3 h-3" />
                          Inactive
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">
                          Not paid
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}

              {status.meals.length === 0 && (
                <div className="py-16 text-center">
                  <p className="text-sm text-gray-500">
                    No meals configured for this event
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* =============================================
              STICKY SUMMARY BAR
          ============================================= */}
          <div className="sticky bottom-4 z-20">
            <div className="bg-white border border-gray-200 rounded-lg shadow-md px-5 py-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-6">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Selected
                    </p>
                    <p className="text-lg font-semibold text-gray-900 mt-0.5">
                      {selected.length}{' '}
                      {selected.length === 1 ? 'meal' : 'meals'}
                    </p>
                  </div>

                  <div className="w-px h-10 bg-gray-200" />

                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Total collected
                    </p>
                    <p className="text-lg font-semibold text-[#1672b9] mt-0.5 inline-flex items-center">
                      <CurrencyRupeeIcon className="w-4 h-4 mr-0.5" />
                      {totalAmount}
                    </p>
                  </div>
                </div>

                <button
                  onClick={handleAssign}
                  disabled={
                    selected.length === 0 ||
                    assignMutation.isPending
                  }
                  className="inline-flex items-center justify-center gap-2 bg-[#1672b9] text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-[#0f5a9e] disabled:opacity-50 disabled:cursor-not-allowed transition-colors sm:min-w-[200px]"
                >
                  {assignMutation.isPending ? (
                    <>
                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      Assigning...
                    </>
                  ) : (
                    <>
                      <CheckIcon className="w-4 h-4" />
                      Assign {selected.length} meal
                      {selected.length === 1 ? '' : 's'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MealAssignmentPanel;